import { inject } from '@angular/core';
import { HttpContextToken, HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { CoreProvider } from '../core.provider';
import { from, switchMap, catchError, throwError, Observable, timeout } from 'rxjs';

export const CUSTOM_REQUEST_AUTHORIZATION = new HttpContextToken<string>(() => '');
export const SKIP_REFRESHRETRY_IF_EXPIRED = new HttpContextToken<boolean>(() => false);
export const CUSTOM_REQUEST_TIMEOUT_TOKEN = new HttpContextToken<number>(() => 30000);

/**
 * Marks a request that must never touch auth. It has to be a header rather than an
 * `HttpContextToken` because the token below cannot express it: its default is `''`,
 * the same value that means "no token yet", so there is no way to say "skip" with it.
 *
 * The point is not just to save an `Authorization` header. Everything past this guard
 * runs `inject(CoreProvider)`, and `CoreProvider` builds the services that would issue
 * such a request in the first place — `VersionService` polling `assets/version.json`
 * during startup would be asking for the hub that is still constructing it.
 */
export const PREVENT_AUTH_HEADER = 'prevent-auth';

export function apiAuthInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  if (req.headers.has(PREVENT_AUTH_HEADER)) {
    return next(req.clone({ headers: req.headers.delete(PREVENT_AUTH_HEADER) }));
  }

  let authHeader = req.context.get(CUSTOM_REQUEST_AUTHORIZATION);
  if (authHeader === CUSTOM_REQUEST_AUTHORIZATION.defaultValue()) {
    const core = inject(CoreProvider);

    return from(core.auth.getLiveTokens()).pipe(
      switchMap(tokens => {
        // console.log('Tokens for request', tokens);
        if (core.auth.isAuthenticated) {
          authHeader = tokens?.accessToken ? `Bearer ${tokens?.accessToken}` : 'INVALID_SESS_STATE';
        }

        return next(req.clone({
          setHeaders: authHeader ? { Authorization: authHeader } : {}
        })).pipe(
          timeout(Number(req.context.get(CUSTOM_REQUEST_TIMEOUT_TOKEN))),
          catchError((error: HttpErrorResponse) => {
            // Check if the error is a 498 status code (token expired)
            if (error.status === 498 && !req.context.get(SKIP_REFRESHRETRY_IF_EXPIRED)) {
              console.log('Received 498 status code, attempting token refresh');

              return from(core.auth.refreshTokens()).pipe(
                switchMap((refreshResult) => {
                  if (refreshResult !== false) {
                    console.log(`Token refresh successful, retrying request to ${req.url}`);
                    // Retry the original request with new tokens
                    return from(core.auth.getLiveTokens()).pipe(
                      switchMap(newTokens => {
                        const newAuthHeader = newTokens?.accessToken ? `Bearer ${newTokens.accessToken}` : '';
                        return next(req.clone({
                          setHeaders: newAuthHeader ? { Authorization: newAuthHeader } : {}
                        })).pipe(
                          timeout(Number(req.context.get(CUSTOM_REQUEST_TIMEOUT_TOKEN))),
                        );
                      })
                    );
                  } else {
                    // Token refresh failed, re-throw the error
                    console.log(`Failed request and attempt to refresh token to ${req.url}`)
                    return throwError(() => error);
                  }
                }),
                catchError((refreshError) => {
                  // Token refresh failed, re-throw the error
                  console.log(`Failed request and attempt to refresh token to ${req.url}`)
                  return throwError(() => error);
                })
              );
            }

            // For other errors, just re-throw
            return throwError(() => error);
          })
        );
      })
    );
  }

  return next(req.clone({
    setHeaders: authHeader ? { Authorization: authHeader } : {}
  })).pipe(
    timeout(Number(req.context.get(CUSTOM_REQUEST_TIMEOUT_TOKEN))),
  );
}
